import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { DocumentChartBarIcon } from '@heroicons/react/24/outline';
import ReportTemplate from '../../components/reports/ReportTemplate';
import CSVExportButton from '../../components/reports/CSVExportButton';
import { ROUTES } from '../../consts/general';
import { useAbortableRequest } from '../../helpers/useAbortableRequest';
import { useInitialMountDelay } from '../../helpers/useInitialMountDelay';
import {
    fetchAllKlaviyoReportData,
    KLAVIYO_REPORT_TABLES,
    renderKlaviyoCell,
} from './klaviyoReportHelpers';

const PAGE_SIZE = 50;

const KlaviyoTableReport = ({ marginFromSidebar, table }) => {
    const navigate = useNavigate();
    const { beginRequest, isLatest } = useAbortableRequest();
    const initialReady = useInitialMountDelay(500);

    const tableConfig = KLAVIYO_REPORT_TABLES[table];
    const columns = useMemo(() => tableConfig?.columns || [], [tableConfig]);

    const [data, setData] = useState([]);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const timeoutId = setTimeout(() => setDebouncedSearch(search.trim()), 300);
        return () => clearTimeout(timeoutId);
    }, [search]);

    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearch, table]);

    const fetchData = useCallback(async () => {
        if (!tableConfig) {
            setError(`Unknown Klaviyo report table: ${table}`);
            setData([]);
            setTotalPages(1);
            setTotalRecords(0);
            return;
        }

        const { signal, requestId } = beginRequest();
        setIsLoading(true);
        setError(null);

        try {
            const { data: response } = await axios.get(`/api/v1/klaviyo/reports/${table}`, {
                signal,
                params: {
                    page: currentPage,
                    limit: PAGE_SIZE,
                    search: debouncedSearch || undefined,
                },
            });

            if (!isLatest(requestId)) return;

            setData(response?.items || []);
            setTotalPages(response?.totalPages || 1);
            setTotalRecords(response?.total || 0);
        } catch (fetchError) {
            if (fetchError?.name === 'AbortError' || fetchError?.code === 'ERR_CANCELED') return;
            console.error(fetchError);
            if (!isLatest(requestId)) return;
            setError(fetchError?.response?.data?.error || fetchError.message || 'Failed to fetch report data');
            setData([]);
            setTotalPages(1);
            setTotalRecords(0);
        } finally {
            if (isLatest(requestId)) {
                setIsLoading(false);
            }
        }
    }, [beginRequest, isLatest, currentPage, debouncedSearch, table, tableConfig]);

    useEffect(() => {
        if (!initialReady) return;
        fetchData();
    }, [initialReady, fetchData]);

    const renderRows = () =>
        (data || []).map((row, rowIndex) => (
            <tr key={`${row.id || rowIndex}-${rowIndex}`} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                {columns.map((column) => (
                    <td
                        key={column.key}
                        className={[
                            'px-6 py-4 text-sm text-gray-900',
                            column.align === 'right' ? 'text-right whitespace-nowrap' : 'whitespace-nowrap',
                            column.key === 'name' || column.key === 'subject_line' || column.key?.includes('audiences')
                                ? 'min-w-[180px] max-w-[320px] break-all !whitespace-normal'
                                : '',
                        ].filter(Boolean).join(' ')}
                    >
                        {renderKlaviyoCell(column, row)}
                    </td>
                ))}
            </tr>
        ));

    const filtersComponent = (
        <div className="contents">
            <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={tableConfig?.searchPlaceholder || 'Search...'}
                className="w-40 sm:w-[320px] rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
        </div>
    );

    const handleExportAll = useCallback(async () => {
        const items = await fetchAllKlaviyoReportData({
            table,
            search: debouncedSearch,
            totalRecords,
        });
        return { data: items, columns };
    }, [columns, table, debouncedSearch, totalRecords]);

    const exportButton = (
        <CSVExportButton
            data={data}
            columns={columns}
            filename={table}
            disabled={isLoading || !totalRecords}
            onExport={handleExportAll}
        />
    );

    const paginationComponent =
        totalPages > 1 ? (
            <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                    Showing {(currentPage - 1) * PAGE_SIZE + 1} to {Math.min(currentPage * PAGE_SIZE, totalRecords)} of{' '}
                    {totalRecords} results
                </div>
                <div className="flex items-center space-x-2">
                    <button
                        type="button"
                        onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:bg-gray-100 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                        Previous
                    </button>
                    <span className="text-sm text-gray-700">
                        Page {currentPage} of {totalPages}
                    </span>
                    <button
                        type="button"
                        onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:bg-gray-100 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                        Next
                    </button>
                </div>
            </div>
        ) : null;

    const displayTitle = tableConfig?.title || table;

    return (
        <ReportTemplate
            title={displayTitle}
            reportId={table}
            description={tableConfig?.description || `Data from ${table}.`}
            breadcrumbs={[
                { label: 'Reports', icon: <DocumentChartBarIcon className="h-5 w-5 text-gray-400" /> },
                { label: displayTitle },
            ]}
            onBack={() => navigate(ROUTES.REPORTS)}
            marginFromSidebar={marginFromSidebar}
            isLoading={isLoading}
            error={error}
            filters={filtersComponent}
            exportButton={exportButton}
            data={data}
            columns={columns}
            pagination={paginationComponent}
            emptyMessage={`No rows found in ${displayTitle}.`}
            enableSorting={false}
            isFirstColumnSticky
        >
            {renderRows()}
        </ReportTemplate>
    );
};

export default KlaviyoTableReport;
